import { Box, Button, Flex, Grid, GridItem, Heading, Spinner, VStack } from '@chakra-ui/react';
import { Logo } from 'components/atoms/Logo';
import { environments } from 'environments/environments';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useState } from 'react';
import Card from './components/Card';

interface ServiceStatus {
    name: string;
    status: 'UP' | 'DOWN' | 'UNKNOWN';
}

const Disponibilidade: React.FC = () => {
    const [servicesStatus, setServicesStatus] = useState<ServiceStatus[]>([]);
    const [loading, setLoading] = useState(true);

    const checkServicesStatus = useCallback(() => {
        setLoading(true);

        const servicesToCheck = [
            { name: 'Contratos', url: environments.ENDPOINT_HEALTH_CONTRACTS },
            { name: 'Agentes Financeiros', url: environments.ENDPOINT_HEALTH_AGENTS },
            { name: 'Instituições Financeiras', url: environments.ENDPOINT_HEALTH_INSTITUTIONS },
            { name: 'Usuários', url: environments.ENDPOINT_HEALTH_USERS },
            { name: 'Relatórios', url: environments.ENDPOINT_HEALTH_REPORTS },
            { name: 'Detran ES', url: environments.ENDPOINT_HEALTH_ES },
        ];

        const fetchStatus = async (service: { name: string; url: string }): Promise<ServiceStatus> => {
            try {
                const response = await fetch(service.url);
                if (!response.ok) {
                    throw new Error(`HTTP status ${response.status}`);
                }
                const data = await response.json();
                if (data && data.status === 'UP') {
                    return { name: service.name, status: 'UP' };
                } else {
                    return { name: service.name, status: 'DOWN' };
                }
            } catch (error) {
                console.error(`Erro ao verificar o serviço ${service.name}:`, error);
                return { name: service.name, status: 'DOWN' };
            }
        };

        Promise.all(servicesToCheck.map(fetchStatus))
            .then(results => {
                setServicesStatus(results);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        checkServicesStatus();
    }, [checkServicesStatus]);

    const Loading = () => (
        <Box
            as={motion.div}
            bg="white"
            borderTopRadius="8px"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            height="100%"
            position="relative"
        >
            <Spinner
                size="xl"
                speed="1s"
                position="absolute"
                top="calc(50% - 33px)"
                left="calc(50% - 33px)"
            />
        </Box>
    );

    return (
        <Box
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            height="100%"
            display="flex"
            flexDir="column"
        >
            <Flex
                px={{ base: '4', md: '6' }}
                py="4"
                align="center"
                justify="space-between"
                bg="white"
                boxShadow="sm"
                mb="8"
            >
                <Box flex="1" />

                <VStack spacing="2">
                    <Logo type="default" h={{ base: '30px', md: '40px' }} w="auto" />
                    <Heading as="h1" size={{ base: 'md', md: 'lg' }} color="gray.700" textAlign="center">
                        Disponibilidade dos Serviços
                    </Heading>
                </VStack>

                <Flex flex="1" justify="flex-end">
                    <Button
                        colorScheme="primary"
                        onClick={checkServicesStatus}
                        isLoading={loading}
                    >
                        Atualizar
                    </Button>
                </Flex>
            </Flex>
            
            {loading && servicesStatus.length === 0 ? (
                <Loading />
            ) : (
                <Box
                    as={motion.div}
                    data-testid="status-box"
                    px="50px"
                     pb="33px"
                    overflowY="auto"
                    flex="5"
                    minWidth={5}
                >
                    <Grid
                        data-testid="services-status"
                        templateColumns="repeat(auto-fill, minmax(350px, 1fr))"
                        gap={6}
                    >
                        {servicesStatus.map((service, index) => (
                            <GridItem key={index} w="100%">
                                <Card name={service.name} status={service.status} />
                            </GridItem>
                        ))}
                    </Grid>
                </Box>
            )}
        </Box>
    );
};

export default Disponibilidade;